import {
  column,
  Css,
  emptyCell,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  IconButton,
  multiFilter,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Link } from "react-router-dom";
import { SearchBox } from "src/components/SearchBox";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { createDesignPackagesUrl, createDesignPackageUrl, createEditPersonalizationOptionUrl } from "src/RouteUrls";
import { isDefined, queryResult } from "src/utils";
import {
  DesignPackagePersonalizationGroupsPage_DesignPackageFragment,
  DesignPackagePersonalizationGroupsPage_EnumDetailsFragment,
  DesignPackagePersonalizationGroupsPage_OptionGroupFragment,
  useDeactivatePersonalizationGroupMutation,
  useDesignPackagePersonalizationGroupsPageQuery,
} from "./DesignPackagePersonalizationGroupsPage.generated";

type DesignPackagePersonalizationGroupsUrlParams = {
  designPackageId: string;
  designPackageVersionId?: string;
};

export function DesignPackagePersonalizationGroupsPage() {
  const { designPackageId, designPackageVersionId } = useParams<DesignPackagePersonalizationGroupsUrlParams>();
  const navigateToRpav = useNavigateToRpav();
  const [deactivate] = useDeactivatePersonalizationGroupMutation();
  const query = useDesignPackagePersonalizationGroupsPageQuery({
    variables: { designPackageId, versionId: designPackageVersionId },
  });

  return queryResult(query, ({ designPackage, enumDetails }) => (
    <>
      <PageHeader
        title="Personalization Groups"
        breadcrumb={[
          { href: createDesignPackagesUrl(), label: "Design Packages" },
          {
            href: createDesignPackageUrl(designPackageId, designPackage.version.id),
            label: designPackage.displayName,
          },
        ]}
      />
      <div css={Css.df.fdc.w75.$}>
        <DesignPackagePersonalizationGroupsTable
          designPackage={designPackage}
          enumDetails={enumDetails}
          deactivate={async (rpogId) => {
            const { data } = await deactivate({ variables: { rpogId } });
            await query.refetch();
            navigateToRpav(data?.savePersonalizationOptionGroup.version.id);
          }}
        />
      </div>
    </>
  ));
}

type FilterOptions = Partial<{
  optionType: ReadyPlanOptionGroupPersonalizationType[];
  location: string[];
}>;

type DesignPackagePersonalizationGroupsTableProps = {
  designPackage: DesignPackagePersonalizationGroupsPage_DesignPackageFragment;
  enumDetails: DesignPackagePersonalizationGroupsPage_EnumDetailsFragment;
  deactivate: (rpogId: string) => Promise<void>;
};
function DesignPackagePersonalizationGroupsTable({
  designPackage,
  enumDetails,
  deactivate,
}: DesignPackagePersonalizationGroupsTableProps) {
  const { openModal } = useModal();
  const history = useHistory();
  const [filter, setFilter] = useState<FilterOptions>({});
  const [search, setSearch] = useState("");

  const filterDefs = useMemo(
    () => ({
      optionType: multiFilter({
        options: enumDetails.readyPlanOptionGroupPersonalizationType,
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.code,
      }),
      location: multiFilter({
        options: designPackage.optionGroups
          .map((rpo) => rpo.location)
          .compact()
          .unique(),
        getOptionLabel: (o) => o.name,
        getOptionValue: (o) => o.id,
      }),
    }),
    [designPackage.optionGroups, enumDetails.readyPlanOptionGroupPersonalizationType],
  );

  const onDeactivate = useCallback(
    async (rpog: DesignPackagePersonalizationGroupsPage_OptionGroupFragment) => {
      openModal({
        size: "lg",
        content: (
          <ConfirmDeactivationModal
            rpog={rpog}
            designPackageId={designPackage.id}
            designPackageVersionId={designPackage.version.id}
            onConfirm={deactivate}
          />
        ),
      });
    },
    [deactivate, designPackage, openModal],
  );

  const columns = useMemo(
    () => createColumns(history, designPackage.id, designPackage.version.id, onDeactivate),
    [designPackage.id, designPackage.version.id, history, onDeactivate],
  );
  const rows = useMemo(() => createRows(designPackage.optionGroups, filter), [designPackage.optionGroups, filter]);

  return (
    <>
      <TableActions>
        <Filters filterDefs={filterDefs} filter={filter} onChange={setFilter} />
        <SearchBox onSearch={setSearch} />
      </TableActions>
      <GridTable columns={columns} rows={rows} sorting={{ on: "client" }} filter={search} />
    </>
  );
}

type HeaderRow = { kind: "header" };
type OptionGroupRow = { kind: "optionGroup"; data: DesignPackagePersonalizationGroupsPage_OptionGroupFragment };
type Row = HeaderRow | OptionGroupRow;

function createColumns(
  history: ReturnType<typeof useHistory>,
  designPackageId: string,
  designPackageVersionId: string,
  onDeactivate: (rpog: DesignPackagePersonalizationGroupsPage_OptionGroupFragment) => void,
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Name",
      optionGroup: ({ name }) => name,
    }),
    column<Row>({
      header: "Option Type",
      optionGroup: ({ personalizationType }) => personalizationType?.name,
    }),
    column<Row>({
      header: "Location",
      optionGroup: ({ location }) => location?.name,
    }),
    column<Row>({
      header: {
        content: "Number of Slots",
        tooltip: "Per package for packages",
      },
      optionGroup: ({ personalizationPlaceholders }) =>
        personalizationPlaceholders
          .map((pp) => pp.slot.id)
          .compact()
          .unique().length,
    }),
    column<Row>({
      header: {
        content: "Number of Material Variants",
        tooltip: "Number of Packages",
      },
      optionGroup: ({ options }) =>
        options
          .map((o) => o.materialVariant)
          .compact()
          .unique().length,
    }),
    column<Row>({
      header: emptyCell,
      optionGroup: (rpog) => (
        <div css={Css.df.gap1.$}>
          <IconButton
            icon="pencil"
            onClick={() => {
              // We don't want to navigate to the slot editor page with all the slots selected because it's weird to see duplicates
              // Override the selected slots with the slot from the personalization placeholder if it exists
              const slotId = rpog.personalizationPlaceholders.map((pp) => pp.slot.id).first ?? "";
              history.push(
                `${createEditPersonalizationOptionUrl(designPackageId, designPackageVersionId, rpog.id)}?slotIds[]=${slotId}`,
              );
            }}
            openInNew
          />
          <IconButton icon="trash" onClick={() => onDeactivate(rpog)} />
        </div>
      ),
      align: "right",
      clientSideSort: false,
    }),
  ];
}

function createRows(
  rpogs: DesignPackagePersonalizationGroupsPage_OptionGroupFragment[],
  filter: FilterOptions,
): GridDataRow<Row>[] {
  const rows = rpogs
    .filter((rpog) => isDefined(rpog.personalizationType) && rpog.active)
    .filter((rpog) => !filter.location || filter.location.isEmpty || filter.location.includes(rpog.location?.id ?? ""))
    .filter(
      (rpog) =>
        !filter.optionType ||
        filter.optionType.isEmpty ||
        filter.optionType.includes(rpog.personalizationType?.code as ReadyPlanOptionGroupPersonalizationType),
    )
    .sortBy((rpog) => rpog.name)
    .map((rpog) => ({ kind: "optionGroup" as const, id: rpog.id, data: rpog }));

  return [simpleHeader, ...rows];
}

type ConfirmDeactivationModalProps = {
  rpog: DesignPackagePersonalizationGroupsPage_OptionGroupFragment;
  designPackageId: string;
  designPackageVersionId: string;
  onConfirm: (rpogId: string) => void;
};
function ConfirmDeactivationModal({
  rpog,
  designPackageId,
  designPackageVersionId,
  onConfirm,
}: ConfirmDeactivationModalProps) {
  return (
    <ConfirmationModal
      confirmationMessage={
        <div>
          Are you sure you want to deactivate this personalization group?
          <p css={Css.sm.gray900.mb2.$}>These are the slots that will be impacted:</p>
          <div css={Css.df.fdc.gap1.$}>
            {rpog.personalizationPlaceholders.map((pp) => (
              <Link
                key={pp.slot.id}
                to={`${createDesignPackageUrl(designPackageId, designPackageVersionId)}?slotIds[]=${pp.slot.id}`}
                target="_blank"
              >
                <div css={Css.df.gap1.blue700.onHover.blue500.cursorPointer.$}>
                  <Icon icon="linkExternal" />
                  <div key={pp.slot.id}>{pp.slot.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      }
      onConfirmAction={() => onConfirm(rpog.id)}
      title={`Deactivate ${rpog.name}?`}
      label="Deactivate"
      danger
    />
  );
}
